// DynamoDB service via Amplify Gen 2 GraphQL client
// Wraps all Amplify Gen 2 data models

import { Amplify } from 'aws-amplify';
import { generateClient } from 'aws-amplify/data';

let _client = null;

function getClient() {
  if (!_client) {
    const config = Amplify.getConfig();
    console.log('[db] Amplify config at client init:', {
      hasAuth: !!config.Auth?.Cognito?.userPoolId || !!config.auth?.user_pool_id,
      hasData: !!config.API?.GraphQL?.endpoint || !!config.data?.url,
    });
    _client = generateClient({ authMode: 'userPool' });
    if (!_client.models) {
      console.error('[db] generateClient() returned no models — model introspection schema may be missing from Amplify config');
    }
  }
  return _client;
}

// Amplify filters each scanned page separately, so a match can sit behind nextToken
// even when the first page comes back empty. Always follow every page.
async function listAll(model, options = {}) {
  const data = [];
  const errors = [];
  let nextToken;
  do {
    const result = await model.list({ ...options, nextToken });
    if (result.errors?.length) errors.push(...result.errors);
    data.push(...(result.data || []));
    nextToken = result.nextToken;
  } while (nextToken);
  return { data, errors: errors.length ? errors : undefined };
}

// ── Users ──

export async function getUser(userId) {
  const { data, errors } = await getClient().models.Users.get({ userId });
  if (errors) console.error('[db] getUser errors:', errors);
  return data;
}

export async function getUserByCognitoId(cognitoId) {
  const { data, errors } = await listAll(getClient().models.Users, {
    filter: { cognitoId: { eq: cognitoId } },
  });
  if (errors?.length) throw new Error(`getUserByCognitoId failed: ${errors.map((e) => e.message).join('; ')}`);
  return data?.[0] || null;
}

export async function getUserByEmail(email) {
  const { data, errors } = await listAll(getClient().models.Users, {
    filter: { email: { eq: email.toLowerCase() } },
  });
  if (errors?.length) throw new Error(`getUserByEmail failed: ${errors.map((e) => e.message).join('; ')}`);
  return data?.[0] || null;
}

export async function listUsers() {
  const users = [];
  let nextToken;
  do {
    const result = await getClient().models.Users.list({ nextToken });
    if (result.errors?.length) throw new Error(result.errors.map(e => e.message).join('; '));
    users.push(...(result.data || []));
    nextToken = result.nextToken;
  } while (nextToken);
  return users;
}

export async function createUser(input) {
  const { data, errors } = await getClient().models.Users.create(input);
  if (errors) console.error('[db] createUser errors:', errors);
  return data;
}

export async function updateUser(userId, updates) {
  const { data, errors } = await getClient().models.Users.update({
    userId,
    ...updates,
  });
  if (errors) console.error('[db] updateUser errors:', errors);
  return data;
}

export async function deleteUser(userId) {
  const { data, errors } = await getClient().models.Users.delete({ userId });
  if (errors) console.error('[db] deleteUser errors:', errors);
  return data;
}

// ── MasterEvents ──

export async function getMasterEvent(eventId) {
  const { data, errors } = await getClient().models.MasterEvents.get({ eventId });
  if (errors) console.error('[db] getMasterEvent errors:', errors);
  return data;
}

export async function listMasterEvents() {
  const { data, errors } = await listAll(getClient().models.MasterEvents);
  if (errors) console.error('[db] listMasterEvents errors:', errors);
  return data || [];
}

export async function createMasterEvent(input) {
  const { data, errors } = await getClient().models.MasterEvents.create(input);
  if (errors) console.error('[db] createMasterEvent errors:', errors);
  return data;
}

export async function updateMasterEvent(eventId, updates) {
  const { data, errors } = await getClient().models.MasterEvents.update({
    eventId,
    ...updates,
  });
  if (errors?.length) {
    console.error('[db] updateMasterEvent errors:', errors);
    throw new Error(errors.map((e) => e.message).join('; '));
  }
  return data;
}

export async function deleteMasterEvent(eventId) {
  const { data, errors } = await getClient().models.MasterEvents.delete({ eventId });
  if (errors) console.error('[db] deleteMasterEvent errors:', errors);
  return data;
}

// ── Memberships ──

export async function getMembership(masterEventId, userId) {
  const { data, errors } = await getClient().models.Memberships.get({
    masterEventId,
    userId,
  });
  if (errors) console.error('[db] getMembership errors:', errors);
  return data;
}

export async function listMemberships() {
  const { data, errors } = await listAll(getClient().models.Memberships);
  if (errors) console.error('[db] listMemberships errors:', errors);
  return data || [];
}

export async function listMembershipsByEvent(masterEventId) {
  const { data, errors } = await listAll(getClient().models.Memberships, {
    filter: { masterEventId: { eq: masterEventId } },
  });
  if (errors) console.error('[db] listMembershipsByEvent errors:', errors);
  return data || [];
}

export async function listMembershipsByUser(userId) {
  const { data, errors } = await listAll(getClient().models.Memberships, {
    filter: { userId: { eq: userId } },
  });
  if (errors) console.error('[db] listMembershipsByUser errors:', errors);
  return data || [];
}

export async function createMembership(input) {
  const { data, errors } = await getClient().models.Memberships.create(input);
  if (errors?.length) {
    console.error('[db] createMembership errors:', errors);
    throw new Error(errors.map((e) => e.message).join('; '));
  }
  return data;
}

export async function updateMembership(masterEventId, userId, updates) {
  const { data, errors } = await getClient().models.Memberships.update({
    masterEventId,
    userId,
    ...updates,
  });
  if (errors?.length) {
    console.error('[db] updateMembership errors:', errors);
    throw new Error(errors.map((e) => e.message).join('; '));
  }
  return data;
}

export async function deleteMembership(masterEventId, userId) {
  const { data, errors } = await getClient().models.Memberships.delete({
    masterEventId,
    userId,
  });
  if (errors?.length) {
    console.error('[db] deleteMembership errors:', errors);
    throw new Error(errors.map((e) => e.message).join('; '));
  }
  return data;
}

// ── Activities ──

export async function getActivity(activityId) {
  const { data, errors } = await getClient().models.Activities.get({ activityId });
  if (errors) console.error('[db] getActivity errors:', errors);
  return data;
}

export async function listActivitiesByEvent(masterEventId) {
  const { data, errors } = await listAll(getClient().models.Activities, {
    filter: { masterEventId: { eq: masterEventId } },
  });
  if (errors) console.error('[db] listActivitiesByEvent errors:', errors);
  return data || [];
}

function serializeJson(value) {
  if (value === null || value === undefined) return value;
  return typeof value === 'string' ? value : JSON.stringify(value);
}

function buildActivityPayload(input) {
  const payload = {};
  const fields = [
    'activityId', 'masterEventId', 'type', 'name', 'description', 'status',
    'isTeamBased', 'isCompetition', 'date', 'image', 'imageType', 'location', 'transportMode',
    'accommodationType', 'hint', 'obfuscated', 'revealed', 'pointsConfig', 'bettingEventId',
    'order', 'concludedAt', 'activityEditors',
  ];
  for (const f of fields) {
    if (f in input) payload[f] = input[f];
  }
  if ('location' in payload) payload.location = serializeJson(payload.location);
  if ('pointsConfig' in payload) payload.pointsConfig = serializeJson(payload.pointsConfig);
  return payload;
}

export async function createActivity(input) {
  const payload = buildActivityPayload(input);
  const { data, errors } = await getClient().models.Activities.create(payload);
  if (errors?.length) {
    console.error('[db] createActivity errors:', errors);
    throw new Error(errors.map((e) => e.message).join('; '));
  }
  return data;
}

export async function updateActivity(activityId, updates) {
  const payload = buildActivityPayload({ ...updates, activityId });
  const { data, errors } = await getClient().models.Activities.update(payload);
  if (errors?.length) {
    console.error('[db] updateActivity errors:', errors);
    throw new Error(errors.map((e) => e.message).join('; '));
  }
  return data;
}

export async function deleteActivity(activityId) {
  const { data, errors } = await getClient().models.Activities.delete({ activityId });
  if (errors?.length) {
    console.error('[db] deleteActivity errors:', errors);
    throw new Error(errors.map((e) => e.message).join('; '));
  }
  return data;
}

// ── FeedItems ──

export async function listFeedItemsByEvent(masterEventId) {
  const { data, errors } = await listAll(getClient().models.FeedItems, {
    filter: { masterEventId: { eq: masterEventId } },
  });
  if (errors?.length) {
    console.error('[db] listFeedItemsByEvent errors:', errors);
    throw new Error(errors.map((e) => e.message).join('; '));
  }
  return data || [];
}

export async function listFeedItemsByActivity(activityId) {
  const { data, errors } = await listAll(getClient().models.FeedItems, {
    filter: { activityId: { eq: activityId } },
  });
  if (errors?.length) {
    console.error('[db] listFeedItemsByActivity errors:', errors);
    throw new Error(errors.map((e) => e.message).join('; '));
  }
  return data || [];
}

export async function createFeedItem(input) {
  const { data, errors } = await getClient().models.FeedItems.create(input);
  if (errors?.length) {
    console.error('[db] createFeedItem errors:', errors);
    throw new Error(errors.map((e) => e.message).join('; '));
  }
  return data;
}

export async function updateFeedItem(feedItemId, updates) {
  const { data, errors } = await getClient().models.FeedItems.update({ feedItemId, ...updates });
  if (errors?.length) {
    console.error('[db] updateFeedItem errors:', errors);
    throw new Error(errors.map((e) => e.message).join('; '));
  }
  return data;
}

export async function deleteFeedItem(feedItemId) {
  const { data, errors } = await getClient().models.FeedItems.delete({ feedItemId });
  if (errors?.length) {
    console.error('[db] deleteFeedItem errors:', errors);
    throw new Error(errors.map((e) => e.message).join('; '));
  }
  return data;
}

// ── Notifications ──

export async function createNotification(input) {
  const { data, errors } = await getClient().models.Notifications.create(input);
  if (errors) console.error('[db] createNotification errors:', errors);
  return data;
}

export async function updateNotification(notificationId, updates) {
  const { data, errors } = await getClient().models.Notifications.update({ notificationId, ...updates });
  if (errors) console.error('[db] updateNotification errors:', errors);
  return data;
}

// ── Teams ──

export async function createTeam(input) {
  const { data, errors } = await getClient().models.Teams.create(input);
  if (errors) console.error('[db] createTeam errors:', errors);
  return data;
}

export async function updateTeam(teamId, updates) {
  const { data, errors } = await getClient().models.Teams.update({ teamId, ...updates });
  if (errors) console.error('[db] updateTeam errors:', errors);
  return data;
}

export async function deleteTeam(teamId) {
  const { data, errors } = await getClient().models.Teams.delete({ teamId });
  if (errors) console.error('[db] deleteTeam errors:', errors);
  return data;
}

export async function listTeamsByEvent(masterEventId) {
  const { data, errors } = await listAll(getClient().models.Teams, {
    filter: { masterEventId: { eq: masterEventId } },
  });
  if (errors) console.error('[db] listTeamsByEvent errors:', errors);
  return data || [];
}

// ── ActivityScores ──

export async function createActivityScore(input) {
  const { data, errors } = await getClient().models.ActivityScores.create(input);
  if (errors) console.error('[db] createActivityScore errors:', errors);
  return data;
}

export async function deleteActivityScore(scoreId) {
  const { data, errors } = await getClient().models.ActivityScores.delete({ scoreId });
  if (errors) console.error('[db] deleteActivityScore errors:', errors);
  return data;
}

export async function listScoresByEvent(masterEventId) {
  const { data, errors } = await listAll(getClient().models.ActivityScores, {
    filter: { masterEventId: { eq: masterEventId } },
  });
  if (errors) console.error('[db] listScoresByEvent errors:', errors);
  return data || [];
}

export async function listScoresByActivity(activityId) {
  const { data, errors } = await listAll(getClient().models.ActivityScores, {
    filter: { activityId: { eq: activityId } },
  });
  if (errors) console.error('[db] listScoresByActivity errors:', errors);
  return data || [];
}

// ── Quizzes ──

export async function createQuiz(input) {
  const { data, errors } = await getClient().models.Quizzes.create(input);
  if (errors) console.error('[db] createQuiz errors:', errors);
  return data;
}

export async function updateQuiz(quizId, updates) {
  const { data, errors } = await getClient().models.Quizzes.update({ quizId, ...updates });
  if (errors) console.error('[db] updateQuiz errors:', errors);
  return data;
}

// ── Votes ──

export async function createVote(input) {
  const { data, errors } = await getClient().models.Votes.create(input);
  if (errors) console.error('[db] createVote errors:', errors);
  return data;
}

export async function updateVote(voteId, updates) {
  const { data, errors } = await getClient().models.Votes.update({ voteId, ...updates });
  if (errors) console.error('[db] updateVote errors:', errors);
  return data;
}

export async function deleteVote(voteId) {
  const { data, errors } = await getClient().models.Votes.delete({ voteId });
  if (errors) console.error('[db] deleteVote errors:', errors);
  return data;
}

// ── PokerGames ──

export async function createPokerGame(input) {
  const { data, errors } = await getClient().models.PokerGames.create(input);
  if (errors) console.error('[db] createPokerGame errors:', errors);
  return data;
}

export async function updatePokerGame(pokerId, updates) {
  const { data, errors } = await getClient().models.PokerGames.update({ pokerId, ...updates });
  if (errors) console.error('[db] updatePokerGame errors:', errors);
  return data;
}

// ── ShuffleboardGames ──

export async function getShuffleboardGame(shuffleboardId) {
  const { data, errors } = await getClient().models.ShuffleboardGames.get({ shuffleboardId });
  if (errors) console.error('[db] getShuffleboardGame errors:', errors);
  return data;
}

export async function getShuffleboardGameByActivity(activityId) {
  const { data, errors } = await listAll(getClient().models.ShuffleboardGames, {
    filter: { activityId: { eq: activityId } },
  });
  if (errors) console.error('[db] getShuffleboardGameByActivity errors:', errors);
  return data?.[0] || null;
}

export async function createShuffleboardGame(input) {
  const { data, errors } = await getClient().models.ShuffleboardGames.create(input);
  if (errors) console.error('[db] createShuffleboardGame errors:', errors);
  return data;
}

export async function updateShuffleboardGame(shuffleboardId, updates) {
  const { data, errors } = await getClient().models.ShuffleboardGames.update({ shuffleboardId, ...updates });
  if (errors) console.error('[db] updateShuffleboardGame errors:', errors);
  return data;
}

export async function deleteShuffleboardGame(shuffleboardId) {
  const { data, errors } = await getClient().models.ShuffleboardGames.delete({ shuffleboardId });
  if (errors) console.error('[db] deleteShuffleboardGame errors:', errors);
  return data;
}

// ── BettingEvents ──

export async function listBettingEvents() {
  const { data, errors } = await listAll(getClient().models.BettingEvents);
  if (errors) console.error('[db] listBettingEvents errors:', errors);
  return data || [];
}

export async function getBettingEvent(bettingEventId) {
  const { data, errors } = await getClient().models.BettingEvents.get({ bettingEventId });
  if (errors) console.error('[db] getBettingEvent errors:', errors);
  return data;
}

export async function createBettingEvent(input) {
  const { data, errors } = await getClient().models.BettingEvents.create(input);
  if (errors) console.error('[db] createBettingEvent errors:', errors);
  return data;
}

export async function updateBettingEvent(bettingEventId, updates) {
  const { data, errors } = await getClient().models.BettingEvents.update({ bettingEventId, ...updates });
  if (errors) console.error('[db] updateBettingEvent errors:', errors);
  return data;
}

export async function deleteBettingEvent(bettingEventId) {
  const { data, errors } = await getClient().models.BettingEvents.delete({ bettingEventId });
  if (errors) console.error('[db] deleteBettingEvent errors:', errors);
  return data;
}

// ── BettingRounds ──

export async function createBettingRound(input) {
  const { data, errors } = await getClient().models.BettingRounds.create(input);
  if (errors) console.error('[db] createBettingRound errors:', errors);
  return data;
}

export async function updateBettingRound(roundId, updates) {
  const { data, errors } = await getClient().models.BettingRounds.update({ roundId, ...updates });
  if (errors) console.error('[db] updateBettingRound errors:', errors);
  return data;
}

export async function deleteBettingRound(roundId) {
  const { data, errors } = await getClient().models.BettingRounds.delete({ roundId });
  if (errors) console.error('[db] deleteBettingRound errors:', errors);
  return data;
}

export async function getBettingRound(roundId) {
  const { data, errors } = await getClient().models.BettingRounds.get({ roundId });
  if (errors) console.error('[db] getBettingRound errors:', errors);
  return data;
}

export async function listRoundsByEvent(bettingEventId) {
  const { data, errors } = await listAll(getClient().models.BettingRounds, {
    filter: { bettingEventId: { eq: bettingEventId } },
  });
  if (errors) console.error('[db] listRoundsByEvent errors:', errors);
  return data || [];
}

// ── BettingMarkets ──

export async function createBettingMarket(input) {
  const { data, errors } = await getClient().models.BettingMarkets.create(input);
  if (errors) console.error('[db] createBettingMarket errors:', errors);
  return data;
}

export async function updateBettingMarket(marketId, updates) {
  const { data, errors } = await getClient().models.BettingMarkets.update({ marketId, ...updates });
  if (errors) console.error('[db] updateBettingMarket errors:', errors);
  return data;
}

export async function deleteBettingMarket(marketId) {
  const { data, errors } = await getClient().models.BettingMarkets.delete({ marketId });
  if (errors) console.error('[db] deleteBettingMarket errors:', errors);
  return data;
}

export async function listMarketsByRound(roundId) {
  const { data, errors } = await listAll(getClient().models.BettingMarkets, {
    filter: { roundId: { eq: roundId } },
  });
  if (errors) console.error('[db] listMarketsByRound errors:', errors);
  return data || [];
}

// ── Bets ──

export async function createBet(input) {
  const { data, errors } = await getClient().models.Bets.create(input);
  if (errors) console.error('[db] createBet errors:', errors);
  return data;
}

export async function updateBet(betId, updates) {
  const { data, errors } = await getClient().models.Bets.update({ betId, ...updates });
  if (errors) console.error('[db] updateBet errors:', errors);
  return data;
}

export async function deleteBet(betId) {
  const { data, errors } = await getClient().models.Bets.delete({ betId });
  if (errors) console.error('[db] deleteBet errors:', errors);
  return data;
}

export async function listBetsByMarket(marketId) {
  const { data, errors } = await listAll(getClient().models.Bets, {
    filter: { marketId: { eq: marketId } },
  });
  if (errors) console.error('[db] listBetsByMarket errors:', errors);
  return data || [];
}

// ── BettingSnapshots ──

export async function createBettingSnapshot(input) {
  const { data, errors } = await getClient().models.BettingSnapshots.create(input);
  if (errors) console.error('[db] createBettingSnapshot errors:', errors);
  return data;
}

export async function deleteBettingSnapshot(snapshotId) {
  const { data, errors } = await getClient().models.BettingSnapshots.delete({ snapshotId });
  if (errors) console.error('[db] deleteBettingSnapshot errors:', errors);
  return data;
}

// ── BetReactions ──

export async function createBetReaction(input) {
  const { data, errors } = await getClient().models.BetReactions.create(input);
  if (errors) console.error('[db] createBetReaction errors:', errors);
  return data;
}

export async function deleteBetReaction(reactionId) {
  const { data, errors } = await getClient().models.BetReactions.delete({ reactionId });
  if (errors) console.error('[db] deleteBetReaction errors:', errors);
  return data;
}

// ── OpenBets ──

function buildOpenBetPayload(input) {
  const payload = { ...input };
  if (payload.participants !== undefined && typeof payload.participants !== 'string') {
    payload.participants = JSON.stringify(payload.participants);
  }
  if (payload.comments !== undefined && typeof payload.comments !== 'string') {
    payload.comments = JSON.stringify(payload.comments);
  }
  return payload;
}

export async function createOpenBet(input) {
  const { data, errors } = await getClient().models.OpenBets.create(buildOpenBetPayload(input));
  if (errors) console.error('[db] createOpenBet errors:', errors);
  return data;
}

export async function updateOpenBet(openBetId, updates) {
  const { data, errors } = await getClient().models.OpenBets.update(
    buildOpenBetPayload({ openBetId, ...updates })
  );
  if (errors) console.error('[db] updateOpenBet errors:', errors);
  return data;
}

export async function deleteOpenBet(openBetId) {
  const { data, errors } = await getClient().models.OpenBets.delete({ openBetId });
  if (errors) console.error('[db] deleteOpenBet errors:', errors);
  return data;
}

export async function getOpenBet(openBetId) {
  const { data, errors } = await getClient().models.OpenBets.get({ openBetId });
  if (errors) console.error('[db] getOpenBet errors:', errors);
  return data;
}

export async function listOpenBetsByActivity(activityId) {
  const { data, errors } = await listAll(getClient().models.OpenBets, {
    filter: { activityId: { eq: activityId } },
  });
  if (errors) console.error('[db] listOpenBetsByActivity errors:', errors);
  return data || [];
}
